import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import InvoiceService from "../../../service/ERP/Invoice.service";
import { formatMoney } from "../../../utils/util";
import "../jo-invoice/jo-invoice.css";
import "./invoice-layout.css";

const invService = InvoiceService();
const PAGE_STYLE_ID = "invoice-print-page-style";
/* จำนวนแถวสินค้าสูงสุดที่ 1 หน้า A4 รองรับได้
   (297mm - ขอบบน/ล่าง 16mm = 281mm
    - ส่วนหัว/คู่สัญญา/ตารางเงื่อนไข+ขนส่ง ~100mm
    - หัวตารางสินค้า ~8mm
    - ยอดรวม/น้ำหนัก/ธนาคาร/หมายเหตุ/ลายเซ็น ~78mm
    เหลือ ~95mm ต่อแถว 8.5mm => 11 แถว เผื่อที่อยู่ยาว/ชื่อสินค้ายาวไว้เหลือ 9) */
const ITEMS_PER_PAGE = 9;

const EXPORTER = {
  company: "NINE STAR FOOD CO., LTD.",
  address: "54/5 MOO 4, TAMBON KHAOWUA, AMPHOE THAMAI, CHANTHABURI 22120, THAILAND",
  tel: "TEL +66 (0)81 8373092, (0)85 3674408",
};

const num = (v) => Number(v || 0);
const up = (v) => String(v ?? "").toUpperCase();

/* รูปแบบวันที่ : ใช้ของลูกค้าถ้าตั้งไว้ ไม่งั้นใช้ DD/MM/YYYY */
const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

/* ท่อนที่ไม่ใช่ชื่อสถานที่ เช่น EORI / TAX ID / VAT / โทรศัพท์ / อีเมล / รหัสไปรษณีย์
   ต้องไม่ถูกหยิบไปต่อท้ายชื่อเมืองปลายทาง */
const NOT_A_COUNTRY = /(EORI|TAX|VAT|TEL|FAX|E-?MAIL|@|\d)/i;

/* ตัดข้อมูลที่ไม่ใช่ชื่อสถานที่ออกจากชื่อเมือง/ท่าเรือ
   เช่น "KLAIPEDA, LITHUANIA, EORI : LT123572246" -> "KLAIPEDA, LITHUANIA" */
const cleanPlace = (place) =>
  String(place || "")
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter((x) => x && !NOT_A_COUNTRY.test(x))
    .join(", ");

/* ลูกค้าขอให้ระบุ "ประเทศ" ต่อท้ายชื่อเมือง/ท่าเรือด้วย
   ถ้าในข้อความมีชื่อประเทศอยู่แล้วจะไม่เติมซ้ำ */
const withCountry = (place, country) => {
  const p = cleanPlace(place).toUpperCase();
  const c = cleanPlace(country).toUpperCase();
  if (!p || !c) return p;
  return p.includes(c) ? p : `${p}, ${c}`;
};

/* Incoterms กลุ่มที่อ้างอิง "เมืองปลายทาง" (ผู้ขายจ่ายค่าขนส่งถึงปลายทาง)
   นอกจากนี้ถือเป็นกลุ่มต้นทาง ใช้ท่าเรือต้นทาง เช่น EXW / FCA / FAS / FOB */
const DESTINATION_TERMS = ["CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const isDestinationTerm = (term) => {
  const t = String(term || "").trim().toUpperCase();
  return DESTINATION_TERMS.some((x) => t.startsWith(x));
};

/* รหัส Incoterms ทั้งหมดที่ใช้จริง (รวม CNF / C&F ที่เขียนกันคนละแบบ) */
const ALL_TERMS = [
  "EXW", "FCA", "FAS", "FOB",
  "CFR", "CNF", "C&F", "CIF", "CPT", "CIP",
  "DAP", "DPU", "DDP", "DAT",
];

/* delivery_term ที่ผู้ใช้กรอกมักมีสถานที่ติดมาด้วย เช่น
   "FOB LAEM CHABANG, THAILAND" หรือ "EXW NINE STAR FOOD FACILITY, THAILAND"
   ถ้าเอาไปต่อกับสถานที่อีกรอบจะได้
   "TOTAL FOB LAEM CHABANG, THAILAND LAEM CHABANG, THAILAND" ซ้ำกันสองรอบ
   จึงแยกเอา "รหัสเทอม" กับ "สถานที่" ออกจากกันก่อนนำไปประกอบใหม่ */
const termCode = (term) => {
  const t = String(term || "").trim().toUpperCase();
  if (!t) return "";
  const first = t.split(/[\s,]+/)[0];
  return ALL_TERMS.includes(first) ? first : "";
};

/* ข้อความหลังรหัสเทอม เช่น "FOB LAEM CHABANG, THAILAND" -> "LAEM CHABANG, THAILAND"
   ถ้าผู้ใช้พิมพ์สถานที่มาเอง ให้เคารพค่าที่พิมพ์ก่อนเสมอ */
const termPlace = (term) => {
  const t = String(term || "").trim();
  const code = termCode(t);
  if (!code) return t.toUpperCase();
  return t.slice(code.length).replace(/^[\s,]+/, "").toUpperCase();
};

/* เดาชื่อประเทศปลายทางจากที่อยู่ผู้รับสินค้า
   ไล่จากท่อนท้ายขึ้นมา ข้ามท่อนที่เป็นเลขทะเบียน/เบอร์โทร */
const guessCountry = (address) => {
  const parts = String(address || "")
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (!NOT_A_COUNTRY.test(parts[i])) return parts[i];
  }
  return "";
};
const fdate = (d, fmt) => {
  if (!d) return "";
  const f = String(fmt || "").trim() || DEFAULT_DATE_FORMAT;
  const out = dayjs(d).format(f);
  /* ถ้ารูปแบบที่กรอกใช้ไม่ได้ ให้ถอยกลับไปใช้ค่าเริ่มต้น */
  return out && out !== "Invalid Date" ? out : dayjs(d).format(DEFAULT_DATE_FORMAT);
};

/* ---- จำนวนเงินเป็นตัวอักษรภาษาอังกฤษ ---- */
const ONES = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE",
  "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN",
  "EIGHTEEN", "NINETEEN"];
const TENS = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];

function below1000(n) {
  let out = "";
  if (n >= 100) {
    out += ONES[Math.floor(n / 100)] + " HUNDRED";
    n %= 100;
    if (n) out += " ";
  }
  if (n >= 20) {
    out += TENS[Math.floor(n / 10)];
    n %= 10;
    if (n) out += " " + ONES[n];
  } else if (n > 0) {
    out += ONES[n];
  }
  return out;
}

function integerToWords(value) {
  let n = Math.floor(num(value));
  if (n === 0) return "ZERO";
  const scales = [[1000000000, "BILLION"], [1000000, "MILLION"], [1000, "THOUSAND"]];
  const parts = [];
  for (const [scale, name] of scales) {
    if (n >= scale) {
      parts.push(below1000(Math.floor(n / scale)) + " " + name);
      n %= scale;
    }
  }
  if (n > 0) parts.push(below1000(n));
  return parts.join(" ").trim();
}

/* อ่านจำนวนเงินเป็นตัวอักษร พร้อมทศนิยม
   2,857.92 -> TWO THOUSAND EIGHT HUNDRED FIFTY SEVEN POINT NINETY TWO
   ถ้าทศนิยมเป็น .00 จะไม่อ่านส่วนทศนิยม */
function amountToWords(amount) {
  const fixed = num(amount).toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  const words = integerToWords(intPart);
  if (decPart === "00") return words;
  /* ทศนิยมที่ขึ้นต้นด้วย 0 ต้องอ่านเลขศูนย์ด้วย เช่น .05 = ZERO FIVE ไม่ใช่ FIVE */
  const decWords =
    decPart[0] === "0"
      ? `ZERO ${integerToWords(decPart[1])}`
      : integerToWords(decPart);
  return `${words} POINT ${decWords}`;
}

function buildPageRows(rows) {
  const pages = [];
  for (let index = 0; index < rows.length; index += ITEMS_PER_PAGE) {
    const chunk = rows.slice(index, index + ITEMS_PER_PAGE);
    while (chunk.length < ITEMS_PER_PAGE) {
      chunk.push({ key: `empty-${index + chunk.length}`, isEmpty: true });
    }
    pages.push(chunk);
  }
  return pages.length > 0
    ? pages
    : [Array.from({ length: ITEMS_PER_PAGE }, (_, index) => ({ key: `empty-${index}`, isEmpty: true }))];
}

function InfoRow({ label, value }) {
  return (
    <div className="iv-inforow">
      <span className="iv-infolabel">{label}</span>
      <span className="iv-infovalue">{value}</span>
    </div>
  );
}

function InvoicePage({
  consignee, notify, meta, bank, pageRows, total, weights,
  isLastPage, pageIndex, totalPages,
}) {
  return (
    <section className={`jo-invoice-page${isLastPage ? " is-last-page" : " has-page-break"}`}>
      {/* ── หัวเอกสาร ── */}
      <header className="iv-top">
        <img src={logo} alt="NSF logo" className="iv-logo" />
        <div className="iv-doctitle">
          COMMERCIAL INVOICE
          {totalPages > 1 && <span className="iv-pageno"> ({pageIndex + 1}/{totalPages})</span>}
        </div>
      </header>
      <div className="iv-rule" />

      {/* ── ผู้ส่งออก + เลขที่เอกสาร ── */}
      <section className="iv-parties">
        <div className="iv-party">
          <div className="iv-party-label">EXPORTER</div>
          <div className="iv-party-body">
            <div className="iv-party-name">{EXPORTER.company}</div>
            <div className="iv-party-line">{EXPORTER.address}</div>
            <div className="iv-party-line">{EXPORTER.tel}</div>
          </div>
        </div>
        <div className="iv-info">
          <InfoRow label="COMMERCIAL INV. NO." value={meta.invoiceNo} />
          <InfoRow label="DATE" value={fdate(meta.date, meta.dateFormat)} />
          <InfoRow label="CUSTOMER ID" value={meta.customerId} />
        </div>
      </section>

      {/* ── ผู้รับสินค้า + Notify party ── */}
      <section className="iv-parties iv-parties-2">
        <div className="iv-party">
          <div className="iv-party-label">CONSIGNEE</div>
          <div className="iv-party-body">
            <div className="iv-party-name">{consignee.name}</div>
            {/* iv-pre = เคารพจุดขึ้นบรรทัดที่ผู้ใช้กดเองในช่องที่อยู่ */}
            <div className="iv-party-line iv-pre">{consignee.address}</div>
            {consignee.contact ? <div className="iv-party-line">Contact: {consignee.contact}</div> : null}
            {consignee.tel ? <div className="iv-party-line">Tel: {consignee.tel}</div> : null}
            {consignee.email ? <div className="iv-party-line">E-mail: {consignee.email}</div> : null}
          </div>
        </div>
        <div className="iv-party">
          <div className="iv-party-label">NOTIFY PARTY</div>
          <div className="iv-party-body">
            {/* บรรทัดแรกคือชื่อบริษัท ทำตัวหนาให้เหมือนช่อง CONSIGNEE */}
            <div className="iv-party-name">{notify.name}</div>
            {notify.lines ? <div className="iv-party-line iv-pre">{notify.lines}</div> : null}
          </div>
        </div>
      </section>

      {/* ── เงื่อนไขการค้า ── */}
      {/* ตัดคอลัมน์ INCOTERMS ออกจากใบพิมพ์ตามที่ลูกค้าแจ้ง
          เพราะเทอมจริงของใบกำกับดูได้จากบรรทัดรวมยอดด้านล่าง (เช่น TOTAL CFR ...)
          ถ้าแสดงตรงนี้ด้วยจะขัดแย้งกันเมื่อราคาต่อหน่วยกับเทอมของใบไม่ใช่ตัวเดียวกัน
          ช่อง INCOTERMS ยังกรอกและดูได้ตามปกติในหน้าคีย์ข้อมูล */}
      <table className="iv-tbl iv-terms">
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "44%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>CUSTOMER P/O REF</th>
            <th>COUNTRY OF ORIGIN</th>
            <th>PAYMENT TERM</th>
            <th>CURRENCY</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="is-center">{meta.customerPo}</td>
            <td className="is-center">{meta.countryOfOrigin}</td>
            <td className="is-center">{meta.paymentTerm}</td>
            <td className="is-center">{meta.currency}</td>
          </tr>
        </tbody>
      </table>

      {/* ── ข้อมูลการขนส่ง ── */}
      <table className="iv-tbl iv-ship">
        <colgroup>
          <col style={{ width: "21%" }} />
          <col style={{ width: "21%" }} />
          <col style={{ width: "32%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <thead>
          {/* ETD กับ ETA แยกคอลัมน์ ไม่รวมช่องเดียวคั่นด้วย / เพื่อให้อ่านชัด */}
          <tr>
            <th>PLACE OF RECEIPT</th>
            <th>FINAL DESTINATION</th>
            <th>CARRIER</th>
            <th>ETD</th>
            <th>ETA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="is-center">{meta.placeOfReceipt}</td>
            <td className="is-center">{meta.finalDestination}</td>
            <td className="is-center iv-pre-line">{meta.carrier}</td>
            <td className="is-center">{fdate(meta.etd, meta.dateFormat)}</td>
            <td className="is-center">{fdate(meta.eta, meta.dateFormat)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── ตารางสินค้า ── */}
      <table className="iv-tbl iv-items">
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "38%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>MARK</th>
            <th>DESCRIPTION OF GOODS</th>
            <th>PACKING SIZE</th>
            <th>QUANTITY ({meta.saleUnit})</th>
            {/* บอกให้ชัดว่าราคาต่อหน่วยเป็นเทอมไหน (FOB / CFR / CIF)
                เพราะมีผลว่าบรรทัดรวมด้านล่างต้องบวกค่าระวาง/ประกันหรือไม่ */}
            <th>
              UNIT PRICE ({meta.currency})
              {meta.priceTermCode ? ` ${meta.priceTermCode}` : ""}
            </th>
            <th>AMOUNT TOTAL ({meta.currency})</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, i) => (
            row.isEmpty ? (
              <tr key={row.key} className="iv-empty">
                <td>&nbsp;</td><td /><td /><td /><td /><td className="iv-amount" />
              </tr>
            ) : (
              <tr key={row.key}>
                <td className="is-center iv-mark iv-pre-line">
                  {pageIndex === 0 && i === 0 ? meta.mark : ""}
                </td>
                {/* BRAND วางไว้หน้าชื่อสินค้าในบรรทัดเดียวกัน และใช้ตัวพิมพ์ใหญ่ทั้งหมด */}
                <td className="is-left iv-desc">
                  {row.brand ? <span className="iv-brand">{up(row.brand)} </span> : null}
                  {up(row.description)}
                </td>
                <td className="is-center">{row.packingSize}</td>
                <td className="is-center">{formatMoney(row.saleQty, 0)}</td>
                <td className="is-right">{formatMoney(row.unitPrice, 2)}</td>
                <td className="is-right iv-amount">{formatMoney(row.amount, 2)}</td>
              </tr>
            )
          ))}
          {/* ป้ายบรรทัดรวมยอด ยืดไปทางซ้าย 3 คอลัมน์ (MARK + DESCRIPTION + PACKING SIZE)
              เพราะข้อความ INCOTERM + สถานที่ ยาวเกินความกว้างคอลัมน์เดียว */}
          {isLastPage && (
            <tr className="iv-total">
              <td colSpan={3} className="is-right iv-total-label">{meta.totalLabel}</td>
              <td className="is-center">{formatMoney(weights.saleQty, 0)}</td>
              <td />
              <td className="is-right iv-amount">{formatMoney(total, 2)}</td>
            </tr>
          )}
          {/* ค่าระวาง / ค่าประกันภัย แยกบรรทัด — พิมพ์เฉพาะช่องที่มีค่า
              ทั้งสองรายการใช้สกุลเงินเดียวกับราคาขายสินค้า */}
          {isLastPage && meta.freightAmount > 0 && (
            <tr className="iv-total iv-freight">
              <td colSpan={3} className="is-right iv-total-label">FREIGHT</td>
              <td />
              <td />
              <td className="is-right iv-amount">{formatMoney(meta.freightAmount, 2)}</td>
            </tr>
          )}
          {isLastPage && meta.insuranceAmount > 0 && (
            <tr className="iv-total iv-freight">
              <td colSpan={3} className="is-right iv-total-label">INSURANCE</td>
              <td />
              <td />
              <td className="is-right iv-amount">{formatMoney(meta.insuranceAmount, 2)}</td>
            </tr>
          )}
          {isLastPage && meta.addOnTotal > 0 && (
            <tr className="iv-total">
              <td colSpan={3} className="is-right iv-total-label">{meta.freightLabel}</td>
              <td />
              <td />
              <td className="is-right iv-amount">
                {formatMoney(total + meta.addOnTotal, 2)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── ท้ายเอกสาร ── */}
      {isLastPage && (
        <>
          {/* จำนวนเงินตัวอักษร ใช้ยอดสุดท้ายที่ลูกค้าต้องจ่ายจริง (รวมค่าระวางถ้ามี) */}
          <div className="iv-words">
            TOTAL {meta.currency} {amountToWords(total + meta.addOnTotal)} ONLY
          </div>

          {/* เหลือแค่ NW / GW และย่อความกว้าง เว้นที่ด้านขวาไว้สำหรับค่า FREIGHT */}
          <table className="iv-tbl iv-weight">
            {/* VOLUME / SHIP VIA พิมพ์เฉพาะใบที่กรอกไว้ */}
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
              {meta.volumeCbm ? <col style={{ width: "18%" }} /> : null}
              {meta.shipVia ? <col style={{ width: "22%" }} /> : null}
            </colgroup>
            <thead>
              <tr>
                <th>NET WEIGHT (KGS)</th>
                <th>GROSS WEIGHT (KGS)</th>
                {meta.volumeCbm ? <th>VOLUME (CBM)</th> : null}
                {meta.shipVia ? <th>SHIP VIA</th> : null}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="is-center">{weights.net > 0 ? formatMoney(weights.net, 2) : ""}</td>
                <td className="is-center">{weights.gross > 0 ? formatMoney(weights.gross, 2) : ""}</td>
                {meta.volumeCbm ? <td className="is-center">{meta.volumeCbm}</td> : null}
                {meta.shipVia ? <td className="is-center">{up(meta.shipVia)}</td> : null}
              </tr>
            </tbody>
          </table>

          <table className="iv-tbl iv-bank">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "78%" }} />
            </colgroup>
            <thead>
              <tr><th colSpan={2}>BANK ACCOUNT DETAILS</th></tr>
            </thead>
            <tbody>
              <tr><td className="iv-bank-key">Account Name:</td><td>{bank.accName}</td></tr>
              <tr>
                <td className="iv-bank-key">Bank Name and Address:</td>
                <td>{[bank.bankName, bank.branch, bank.address].filter(Boolean).join(", ")}</td>
              </tr>
              <tr><td className="iv-bank-key">Account No.:</td><td>{bank.accNo}</td></tr>
              <tr><td className="iv-bank-key">SWIFT Code:</td><td>{bank.swiftCode}</td></tr>
            </tbody>
          </table>

          {meta.remark && (
            <div className="iv-remark">
              <span className="iv-remark-label">REMARK :</span>
              <span className="iv-remark-text">{meta.remark}</span>
            </div>
          )}

          {/* ประโยค declare เดิมมีความหมายซ้ำกับ CERTIFIED TRUE AND CORRECT จึงตัดออก
              เหลือบรรทัดเดียว และย้ายมาอยู่เหนือเส้นลงชื่อตามที่ลูกค้าขอ */}
          {/* ไม่มีช่อง Date ด้านล่างแล้ว วันที่ของใบกำกับอยู่ที่หัวเอกสาร (ช่อง DATE) */}
          <div className="iv-signs iv-signs-right">
            <div className="iv-sign iv-sign-stack">
              <div className="iv-certified">&quot;CERTIFIED TRUE AND CORRECT&quot;</div>
              <div className="iv-sign-row">
                <span className="iv-sign-label">Signature and Stamp:</span>
                <span className="iv-sign-line" />
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function InvoicePrintPreview() {
  const { code } = useParams();

  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);

  const handlePrint = () => window.print();

  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const apply = (v) => {
      htmlStyle.setProperty("overflow", v, "important");
      htmlStyle.setProperty("overflow-y", v, "important");
      bodyStyle.setProperty("overflow", v, "important");
      bodyStyle.setProperty("overflow-y", v, "important");
    };
    apply("auto");

    /* @page scope ตาม element ไม่ได้ และไฟล์ print อื่นถูกรวมมาในบันเดิลเดียวกัน
       จึงฉีดกฎขนาดกระดาษของใบนี้ตอนสั่งพิมพ์ ให้เป็นตัวท้ายสุดเสมอ แล้วถอดออกหลังพิมพ์ */
    const injectPageRule = () => {
      let el = document.getElementById(PAGE_STYLE_ID);
      if (!el) {
        el = document.createElement("style");
        el.id = PAGE_STYLE_ID;
        el.media = "print";
        el.innerHTML = "@page { size: A4 portrait; margin: 0; }";
        document.head.appendChild(el);
      }
    };
    const removePageRule = () => {
      const el = document.getElementById(PAGE_STYLE_ID);
      if (el) el.remove();
    };

    const onBefore = () => {
      injectPageRule();
      apply("visible");
    };
    const onAfter = () => {
      removePageRule();
      apply("auto");
    };
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
      removePageRule();
    };
  }, []);

  useEffect(() => {
    invService
      .get(code)
      .then((res) => {
        const { header: h, detail: d } = res.data?.data || {};
        setHeader(h || {});
        setDetails(d || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "Request failed. Please try again.",
          onOk: () => window.close(),
        });
      });
  }, [code]);


  /* ผู้รับสินค้า — สำเนาที่เลือกไว้ตอนสร้าง invoice */
  const consignee = useMemo(() => ({
    name: header?.consignee_name || "",
    address: header?.consignee_address || "",
    contact: header?.consignee_contact || "",
    email: header?.consignee_email || "",
    tel: header?.consignee_tel || "",
  }), [header]);

  const bankRow = Array.isArray(header?.banks) && header.banks.length > 0 ? header.banks[0] : null;
  const bank = {
    swiftCode: bankRow?.swift_code || header?.swift_code || "",
    accName: bankRow?.acc_name || header?.bank_beneficiary || EXPORTER.company,
    accNo: bankRow?.acc_no || header?.bank_account || "",
    branch: bankRow?.bank_branch || header?.bank_branch || "",
    bankName: bankRow?.bank_name || header?.bank_name || "",
    address: bankRow?.bank_address || header?.bank_address || "",
  };

  /* Notify party — เว้นว่างไว้ให้ใช้ข้อมูล consignee เดียวกัน (รวมเบอร์โทรด้วย)
     บรรทัดแรกใช้เป็นชื่อบริษัท (ตัวหนา) ที่เหลือเป็นที่อยู่ */
  const notify = useMemo(() => {
    const raw =
      (header?.notify_party || "").trim() ||
      [
        consignee.name,
        consignee.address,
        consignee.tel ? `Tel: ${consignee.tel}` : "",
      ]
        .filter(Boolean)
        .join("\n");

    const [first = "", ...rest] = raw.split(/\r?\n/);
    return { name: first.trim(), lines: rest.join("\n").trim() };
  }, [header?.notify_party, consignee]);

  const meta = useMemo(() => ({
    invoiceNo: header?.inv_code || "",
    date: header?.inv_date,
    /* MARK = เครื่องหมายบนหีบห่อ ไม่ใช่ชื่อแบรนด์สินค้า
       ใบเก่าที่ยังไม่มี shipping_mark ให้ถอยไปใช้ brand เพื่อไม่ให้เอกสารย้อนหลังว่าง */
    mark: header?.shipping_mark || header?.brand || "",
    customerPo: header?.customer_po || "",
    customerId: header?.cuscode || "",
    currency: header?.currency || "",
    priceTerm: header?.delivery_term || header?.fob_term || "",
    /* รหัสเทอมล้วน ๆ เช่น FOB / CFR / CIF ใช้กำกับหัวคอลัมน์ UNIT PRICE */
    priceTermCode: termCode(header?.delivery_term || header?.fob_term),
    /* ป้ายแถวรวม : INCOTERM + สถานที่ตามหลักสากล
       - เทอมกลุ่มต้นทาง (EXW/FCA/FAS/FOB) ใช้ "ท่าเรือต้นทาง"
       - เทอมกลุ่มปลายทาง (CFR/CIF/CPT/CIP/DAP/DDP) ใช้ "เมืองปลายทาง"
       สถานที่ที่ผู้ใช้พิมพ์ติดมากับ delivery_term มาก่อน ถ้าไม่ได้พิมพ์จึงเติมให้เอง
       (เดิมต่อสถานที่ทับของเดิมเสมอ เลยได้ TOTAL FOB LAEM CHABANG... LAEM CHABANG... ซ้ำ) */
    totalLabel: (() => {
      const code = termCode(header?.delivery_term);
      const typed = termPlace(header?.delivery_term);
      const auto = isDestinationTerm(header?.delivery_term)
        ? withCountry(
            header?.final_destination,
            header?.destination_country || guessCountry(header?.consignee_address)
          )
        : withCountry(header?.place_of_receipt, header?.country_of_origin || "THAILAND");
      const place = typed || auto;
      return `TOTAL ${[code, place].filter(Boolean).join(" ")}`.trim().toUpperCase();
    })(),
    paymentTerm: header?.payment_term || "",
    portOfLoading: header?.port_of_loading || "",
    placeOfReceipt: withCountry(header?.place_of_receipt, header?.country_of_origin || "THAILAND"),
    finalDestination: withCountry(
      header?.final_destination,
      header?.destination_country || guessCountry(header?.consignee_address)
    ),
    carrier: header?.carrier || "",
    etd: header?.etd,
    eta: header?.eta,
    countryOfOrigin: header?.country_of_origin || "THAILAND",
    /* ค่าระวาง + ค่าประกันภัย (สกุลเดียวกับราคาขาย)
       ป้ายบรรทัดสุดท้าย = เงื่อนไข + เมืองปลายทาง เช่น CIF SINGAPORE */
    freightAmount: num(header?.freight_amount),
    insuranceAmount: num(header?.insurance_amount),
    addOnTotal: num(header?.freight_amount) + num(header?.insurance_amount),
    freightLabel: `${String(
      header?.freight_term || (num(header?.insurance_amount) > 0 ? "CIF" : "CFR")
    ).trim()} ${withCountry(
      header?.final_destination,
      header?.destination_country || guessCountry(header?.consignee_address)
    )}`.trim().toUpperCase(),
    volumeCbm: header?.volume_cbm || "",
    /* วิธีขนส่ง เช่น FCL 1X20' / LCL / BY AIR */
    shipVia: header?.ship_via || "",
    /* หัวคอลัมน์ QUANTITY ใช้หน่วยขายของรายการในใบ
       ถ้าในใบมีหลายหน่วยปนกันจะโชว์ UNIT กลาง ๆ แทนเพื่อไม่ให้สื่อผิด */
    saleUnit: (() => {
      const units = [
        ...new Set(
          (details || [])
            .map((d) => String(d?.sale_unit || "").trim().toUpperCase())
            .filter(Boolean)
        ),
      ];
      if (units.length === 1) return units[0];
      if (units.length === 0) return "CASE";
      return "UNIT";
    })(),
    remark: header?.remark || "",
    dateFormat: header?.cus_date_format || "",
  }), [header, details]);

  const rows = useMemo(() => (details || []).map((item, index) => {
    const cases = num(item?.carton);
    /* จำนวนที่พิมพ์ในคอลัมน์ QUANTITY และใช้คูณราคา = จำนวนหน่วยขาย
       ใบเก่าที่ยังไม่มี sale_qty ให้ถอยไปใช้จำนวนกล่องเหมือนเดิม */
    const saleQty = num(item?.sale_qty) > 0 ? num(item.sale_qty) : cases;
    const unitPrice = num(item?.unit_price);
    const amount = num(item?.line_total) > 0 ? num(item.line_total) : saleQty * unitPrice;
    return {
      key: `${item?.id || "row"}-${index}`,
      itemNo: item?.seq ?? index + 1,
      hsCode: item?.hs_code || "",
      description: item?.pg_name || "",
      /* แบรนด์เป็นรายบรรทัด ใบหนึ่งมีได้หลายแบรนด์
         ถ้าบรรทัดไม่ได้ระบุ จึงถอยไปใช้แบรนด์ค่าตั้งต้นของหัวเอกสาร */
      brand: item?.brand || header?.brand || "",
      packingSize: item?.packing_size || "",
      cases,
      saleQty,
      unitPrice,
      amount,
      _netWeight: cases * num(item?.net_wt),
      /* Gross Weight รวมน้ำหนักพาเลตต่อหน่วยด้วย (พาเลตไม้ 21 / พลาสติก 7.8 kg)
         ส่วน Net Weight เป็นน้ำหนักสินค้าล้วน ไม่รวมพาเลต */
      _grossWeight: cases * (num(item?.gross_wt) + num(item?.pallet_wt)),
    };
  }), [details, header?.brand]);

  const total = useMemo(
    () => (header?.total_amount != null
      ? num(header.total_amount)
      : rows.reduce((s, r) => s + r.amount, 0)),
    [rows, header?.total_amount]
  );

  const weights = useMemo(() => ({
    cartons: rows.reduce((s, r) => s + r.cases, 0),
    /* ยอดรวมของคอลัมน์ QUANTITY = ผลรวมจำนวนหน่วยขายทุกบรรทัด */
    saleQty: rows.reduce((s, r) => s + r.saleQty, 0),
    net: header?.net_weight != null
      ? num(header.net_weight)
      : rows.reduce((s, r) => s + r._netWeight, 0),
    gross: header?.gross_weight != null
      ? num(header.gross_weight)
      : rows.reduce((s, r) => s + r._grossWeight, 0),
  }), [rows, header?.net_weight, header?.gross_weight]);

  const rowPages = useMemo(() => buildPageRows(rows), [rows]);

  return (
    <div className="jo-invoice-screen inv-doc" id="jo-invoice">
      <div className="jo-invoice-toolbar">
        <Button className="bn-center bg-blue-400" onClick={handlePrint} icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}>
          PRINT
        </Button>
      </div>

      {/* ปล่อยความสูงตามจำนวนหน้าจริง (เดิม fix ไว้ n x 297mm ทำให้หน้าท้ายโดนตัด) */}
      <div className="jo-invoice-preview">
        {rowPages.map((pageRows, pageIndex) => (
          <InvoicePage
            key={`invoice-page-${pageIndex}`}
            consignee={consignee}
            notify={notify}
            meta={meta}
            bank={bank}
            pageRows={pageRows}
            total={total}
            weights={weights}
            isLastPage={pageIndex === rowPages.length - 1}
            pageIndex={pageIndex}
            totalPages={rowPages.length}
          />
        ))}
      </div>
    </div>
  );
}

export default InvoicePrintPreview;
